import type { ShipmentContainer, ShipmentDetail, ShipmentDocument } from "api";

/** The Documents tab's segment control, as the design names it. */
export type DocSegment = "all" | "ship" | "ct";

export const DOC_SEGMENTS: { key: DocSegment; label: string }[] = [
  { key: "all", label: "All" },
  { key: "ship", label: "Shipment" },
  { key: "ct", label: "By container" },
];

/** What the back office accepts (`config/shipments.php`); the picker offers only these. */
export const DOCUMENT_ACCEPT = ".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.xls,.xlsx,.csv,.txt,.zip";

/**
 * The back office's `SHIPMENT_DOCS_MAX_KB` default, 20 MB. Checked before
 * sending: a body much past it is cut off by nginx with a bare 413 that
 * carries no CORS headers, so the browser reports a network error instead of
 * the back office's own message.
 */
export const DOCUMENT_MAX_BYTES = 20480 * 1024;

export const DOCUMENT_TOO_LARGE = "This file is larger than 20 MB. Please upload a smaller one.";

export type ContainerDocGroup = {
  container: ShipmentContainer;
  label: string;
  documents: ShipmentDocument[];
};

/**
 * "MSCU7742051 · 40' High Cube", or "· LCL" when the cargo is shared. A row
 * saved before its number was known still holds documents, so it gets a
 * placeholder rather than dropping out of the list.
 */
export const containerLabel = (
  shipment: Pick<ShipmentDetail, "load_type">,
  container: Pick<ShipmentContainer, "number" | "container_type">,
  index: number
): string => {
  // `||`, not `??`: the Cargo tab's form holds an empty string where the API has null.
  const name = container.number || `Container ${index + 1}`;
  const type = shipment.load_type === "LCL" ? "LCL" : container.container_type;

  return type ? `${name} · ${type}` : name;
};

/** Documents that belong to the shipment as a whole, not to a container. */
export const shipmentLevelDocuments = (documents: ShipmentDocument[]): ShipmentDocument[] =>
  documents.filter((document) => document.container_id === null);

/** One group per container, in the grid's order, each with its own documents. */
export const containerDocGroups = (shipment: ShipmentDetail, documents: ShipmentDocument[]): ContainerDocGroup[] =>
  shipment.containers.map((container, index) => ({
    container,
    label: containerLabel(shipment, container, index),
    documents: documents.filter((document) => document.container_id === container.id),
  }));

/** The grey line under "Documents": the numbers the shipment is known by. */
export const documentsSub = (shipment: ShipmentDetail): string =>
  [shipment.master_bl ?? shipment.reference, shipment.booking_number].filter(Boolean).join("  ·  ");
